"use client";

import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, DollarSign, Megaphone, BellRing } from 'lucide-react';

export interface AdSlotProps {
  type: 'google' | 'native' | 'sponsor' | 'promotion';
  position: number;
  animationDelay?: number;
}

const adContent = {
  google: {
    label: 'Advertisement',
    icon: ExternalLink,
    color: 'gray'
  },
  native: {
    label: 'Featured Partner',
    icon: DollarSign,
    color: 'emerald'
  },
  sponsor: {
    label: 'Sponsored',
    icon: Megaphone,
    color: 'purple'
  },
  promotion: {
    label: 'Special Promotion',
    icon: BellRing,
    color: 'amber'
  }
};

// Helper function to get color classes based on ad type
const getAdColorClasses = (type: 'badge' | 'background', color: string) => {
  if (type === 'badge') {
    switch (color) {
      case 'gray': return 'bg-[var(--color-neutral-100)] text-[var(--color-neutral-600)] border-[var(--color-neutral-300)]';
      case 'emerald': return 'bg-[var(--color-sale-secondary)] text-[var(--color-sale-primary)] border-[var(--color-sale-secondary)]';
      case 'purple': return 'bg-[var(--color-sponsored-secondary)] text-[var(--color-sponsored-primary)] border-[var(--color-sponsored-secondary)]';
      case 'amber': return 'bg-[var(--color-featured-secondary)] text-[var(--color-featured-primary)] border-[var(--color-featured-secondary)]';
      default: return 'bg-[var(--color-neutral-100)] text-[var(--color-neutral-600)] border-[var(--color-neutral-300)]';
    }
  }
  
  if (type === 'background') {
    switch (color) {
      case 'gray': return 'from-[var(--color-pure-white)] to-[var(--color-neutral-50)]';
      case 'emerald': return 'from-[var(--color-pure-white)] to-[var(--color-sale-secondary)]/30';
      case 'purple': return 'from-[var(--color-pure-white)] to-[var(--color-sponsored-secondary)]/30';
      case 'amber': return 'from-[var(--color-pure-white)] to-[var(--color-featured-secondary)]/30';
      default: return 'from-[var(--color-pure-white)] to-[var(--color-neutral-50)]';
    }
  }
  
  return '';
};

const SponsoredAdSlot = ({ type, position, animationDelay = 0 }: AdSlotProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const ad = adContent[type];
  
  // Simulate ad loading with proper cleanup
  useEffect(() => {
    // A deterministic delay based on position to avoid client/server mismatch
    const delay = 300 + (position * 150) % 500;
    
    // Set the timer
    timerRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    
    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [position]);
  
  const IconComponent = ad.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ 
        opacity: isVisible ? 1 : 0, 
        scale: isVisible ? 1 : 0.95,
        y: isVisible ? 0 : 10 
      }}
      transition={{ 
        duration: 0.5,
        delay: animationDelay,
        ease: "easeOut" as const
      }}
      className="relative w-full rounded-xl overflow-hidden shadow-md border border-[var(--color-neutral-100)] h-full min-h-56 flex flex-col"
    >
      {/* Ad Label */}
      <div className="absolute top-3 left-3 z-10">
        <Badge className={`
          ${getAdColorClasses('badge', ad.color)} 
          border px-2 py-1 
          text-xs font-medium flex items-center gap-1
        `}>
          <IconComponent className="h-3 w-3" />
          {ad.label}
        </Badge>
      </div>
      
      {/* Content based on ad type */}
      <div className={`
        flex-1 flex flex-col items-center justify-center p-6
        bg-linear-to-br ${getAdColorClasses('background', ad.color)}
      `}>
        {type === 'google' ? (
          <div className="text-center">
            <div className="text-[var(--color-neutral-600)] mb-2 text-sm">Google AdSense</div>
            <div className="bg-[var(--color-neutral-100)] rounded-lg p-6 flex items-center justify-center w-full h-32">
              <span className="text-[var(--color-neutral-600)]">Ad Content</span>
            </div>
          </div>
        ) : type === 'native' ? (
          <div className="text-center">
            <div className="bg-[var(--color-sale-secondary)]/30 rounded-lg p-6 flex flex-col items-center w-full h-32">
              <span className="text-[var(--color-sale-primary)] font-medium mb-2">Partner Content</span>
              <p className="text-sm text-[var(--color-neutral-600)]">High-quality properties from our trusted partners</p>
              <div className="mt-3 bg-[var(--color-sale-secondary)] text-[var(--color-sale-primary)] px-3 py-1 rounded-full text-xs">
                Learn More
              </div>
            </div>
          </div>
        ) : type === 'sponsor' ? (
          <div className="text-center">
            <div className="bg-[var(--color-sponsored-secondary)]/30 rounded-lg p-6 flex flex-col items-center w-full h-32">
              <span className="text-[var(--color-sponsored-primary)] font-medium mb-2">Premium Property Showcase</span>
              <p className="text-sm text-[var(--color-neutral-600)]">Exclusive listings from our sponsors</p>
              <div className="mt-3 bg-[var(--color-sponsored-secondary)] text-[var(--color-sponsored-primary)] px-3 py-1 rounded-full text-xs">
                View Offer
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="bg-[var(--color-featured-secondary)]/30 rounded-lg p-6 flex flex-col items-center w-full h-32">
              <span className="text-[var(--color-featured-primary)] font-medium mb-2">Limited Time Offer</span>
              <p className="text-sm text-[var(--color-neutral-600)]">Special promotion for premium properties</p>
              <div className="mt-3 bg-[var(--color-featured-secondary)] text-[var(--color-featured-primary)] px-3 py-1 rounded-full text-xs">
                Get Deal
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer with ad position */}
      <div className="px-4 py-2 bg-[var(--color-neutral-50)] border-t border-[var(--color-neutral-100)] text-right">
        <span className="text-xs text-[var(--color-neutral-600)]">Ad {position}</span>
      </div>
    </motion.div>
  );
};

export default SponsoredAdSlot; 